/* eslint-disable react-hooks/exhaustive-deps */
import { useReducer, useMemo, useCallback, memo, useEffect } from 'react';
import CompanyContext from './CompanyContext';
import initialCompanyState from './initialCompanyState';
import companyReducer from 'contexts/company/reducer/company.reducer';
import * as action from 'contexts/company/reducer/company.actions';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { ChildrenProps } from 'interfaces/general.interface';
import { TSocialMedia } from 'api/endpoints/company/company.response.interface';
import { ICompanyUpdateFields } from 'interfaces/company.interface';
import useContextUser from 'hooks/contexts/useContextUser';
import { IgetClientsAPIResponseDataItem } from 'api/endpoints/superAdmin/superAdmin.response.interface';

function CompanyProvider(props: ChildrenProps) {
  const [state, dispatch] = useReducer(companyReducer, initialCompanyState);
  const { getAccessTokenSilently } = useAuth0();
  const { dbUser } = useContextUser();
  const { t: translate } = useTranslation();

  useEffect(() => {
    (async () => {
      if (dbUser?.user_clients?.[0]?.client?.client_id && !state.company) {
        const token = await getAccessTokenSilently();
        if (!token) return false;
        return await action.getCompanyDataAction({
          dispatch,
          clientId: dbUser?.user_clients?.[0]?.client?.client_id,
          token,
          translate,
        });
      }
    })();
  }, [state.company, dbUser?.user_clients]);

  const updateCompanyData = useCallback(
    async ({ companyData }: { companyData: ICompanyUpdateFields }) => {
      if (!companyData || !state.company?.id) return false;
      const token = await getAccessTokenSilently();
      return await action.updateCompanyAction({
        dispatch,
        companyId: state.company.id,
        companyData,
        token,
        translate,
      });
    },
    [state.company?.id]
  );

  const updateCompanyLogo = useCallback(
    async ({ image }: { image: File }) => {
      if (!state.company?.id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.updateCompanyLogoAction({
        dispatch,
        clientId: state.company.id,
        image,
        token,
        translate,
      });
    },
    [state.company?.id]
  );

  const updateSocialMedia = useCallback(
    async ({ socialMedia }: { socialMedia: TSocialMedia }) => {
      if (!state.company?.client_social_media) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.updateSocialMediaAction({
        dispatch,
        socialMediaState: state.company?.client_social_media,
        socialMedia,
        token,
        translate,
      });
    },
    [state.company?.client_social_media]
  );

  const removeSocialMedia = useCallback(
    async ({ socialMediaId }: { socialMediaId: string }) => {
      if (!state.company?.client_social_media) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.removeSocialMediaAction({
        dispatch,
        socialMediaState: state.company?.client_social_media,
        socialMediaId,
        token,
        translate,
      });
    },
    [state.company?.client_social_media]
  );

  const selectCompanySuperAdmin = useCallback(
    async ({ client }: { client: IgetClientsAPIResponseDataItem }) => {
      if (!client) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.selectCompanySuperAdminAction({
        dispatch,
        client,
        translate,
        token,
      });
    },
    []
  );

  const memoProvider = useMemo(
    () => ({
      ...state,
      updateCompanyData,
      updateCompanyLogo,
      updateSocialMedia,
      removeSocialMedia,
      selectCompanySuperAdmin,
    }),
    [
      state,
      updateCompanyData,
      updateCompanyLogo,
      updateSocialMedia,
      removeSocialMedia,
      selectCompanySuperAdmin,
    ]
  );

  return (
    <CompanyContext.Provider value={memoProvider}>
      {props.children}
    </CompanyContext.Provider>
  );
}

export default memo(CompanyProvider);
